// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as z from 'zod';
import { User } from '@/account/api/get-organization-users';
import {
  UpdateOrganizationUserParameters,
  updateOrganizationUser,
} from '@/account/api/update-organization-user';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { usePermissionChecker } from '@/authorization/hooks/use-user-policy';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormTextField } from '@/components/form-text-field';
import { PolicyGate } from '@/components/policy-gate';
import {
  Currency,
  SerializableCurrency,
  clearableCurrencyInputSchema,
} from '@/currency/api/types';
import { toCurrencyInputValue } from '@/currency/utilities/to-form-value';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Decimal } from 'decimal.js';
import { SubmitHandler, useForm } from 'react-hook-form';

const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  defaultRate: clearableCurrencyInputSchema.refine(
    // A cleared or unparseable rate already carries its own outcome.
    (value) => !Decimal.isDecimal(value?.amount) || !value.amount.isNegative(),
    { path: ['amount'], message: 'Default hourly rate cannot be negative' }
  ),
});

type FormData = Omit<z.infer<typeof formSchema>, 'defaultRate'> & {
  defaultRate: SerializableCurrency;
};
type FormOutput = z.output<typeof formSchema>;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  organizationKey: string;
}

export function EditOrganizationUserDialog(props: Props) {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissionChecker();
  const { user: currentUser } = useCurrentUser();
  const toRateFormValue = (value: Currency | null): SerializableCurrency =>
    toCurrencyInputValue(value, currentUser.organization.defaultCurrency);
  const updateOrganizationUserMutation = useMutation({
    mutationFn: (parameters: UpdateOrganizationUserParameters) =>
      updateOrganizationUser(
        props.organizationKey,
        props.user!.key,
        parameters
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-users', props.organizationKey],
      });
      props.onClose();
    },
  });

  const { control, formState, reset, handleSubmit } = useForm<
    FormData,
    unknown,
    FormOutput
  >({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: props.user?.firstName || '',
      lastName: props.user?.lastName || '',
      defaultRate: toRateFormValue(props.user?.defaultRate ?? null),
    },
  });

  const isSaveDisabled =
    updateOrganizationUserMutation.isPending || !formState.isDirty;

  const hasAccessToDefaultRate = hasPermission('time.rate', 'update');

  const handleValidSubmission: SubmitHandler<FormOutput> = (data) => {
    const parameters: UpdateOrganizationUserParameters = {};

    if (formState.dirtyFields.firstName) {
      parameters.firstName = data.firstName;
    }

    if (formState.dirtyFields.lastName) {
      parameters.lastName = data.lastName;
    }

    if (hasAccessToDefaultRate && formState.dirtyFields.defaultRate) {
      parameters.defaultRate = data.defaultRate ?? null;
    }

    updateOrganizationUserMutation.mutate(parameters);
  };

  const handleClose = () => {
    reset();
    updateOrganizationUserMutation.reset();
    props.onClose();
  };

  if (!props.user) {
    return null;
  }

  return (
    <Dialog open={props.isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit(handleValidSubmission)}>
        <DialogTitle>Edit user</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ paddingTop: 1 }}>
            <Stack spacing={2}>
              <FormTextField
                name="firstName"
                label="First name"
                control={control}
                disabled={updateOrganizationUserMutation.isPending}
                fullWidth
              />
              <FormTextField
                name="lastName"
                label="Last name"
                control={control}
                disabled={updateOrganizationUserMutation.isPending}
                fullWidth
              />
              <TextField
                size="small"
                label="Email"
                InputLabelProps={{ shrink: true }}
                disabled
                value={props.user.email}
                fullWidth
              />
              <PolicyGate path="time.rate" action="update">
                <FormCurrencyTextField
                  name="defaultRate"
                  label="Default hourly rate"
                  control={control}
                  disabled={updateOrganizationUserMutation.isPending}
                  fullWidth
                />
              </PolicyGate>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={updateOrganizationUserMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isSaveDisabled}>
            {updateOrganizationUserMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
